import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "react-i18next";
import type { EntityType } from "../types/types.MDM";
import { useStoreState } from "./util";

function formatUnknownObjectType(objectType: string): string {
  return objectType
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export type MdmEntityDisplayNameForms = {
  singular: string;
  plural: string;
};

export type MdmEntityDisplayNames = Record<
  EntityType,
  MdmEntityDisplayNameForms
>;

export type MdmEntityDisplayNameOverrides =
  | Partial<Record<EntityType, Partial<MdmEntityDisplayNameForms>>>
  | null
  | undefined;

export const DEFAULT_MDM_ENTITY_DISPLAY_NAMES: MdmEntityDisplayNames = {
  customer: { singular: "Customer", plural: "Customers" },
  supplier: { singular: "Supplier", plural: "Suppliers" },
  distributor: { singular: "Distributor", plural: "Distributors" },
};

export const CANONICAL_MDM_DEFAULT_TEMPLATE_NAMES: Record<EntityType, string> =
  {
    customer: "Customer Default Template",
    supplier: "Supplier Default Template",
    distributor: "Distributor Default Template",
  };

export function resolveMdmEntityDisplayNames(
  overrides?: MdmEntityDisplayNameOverrides
): MdmEntityDisplayNames {
  return {
    customer: {
      singular:
        overrides?.customer?.singular ||
        DEFAULT_MDM_ENTITY_DISPLAY_NAMES.customer.singular,
      plural:
        overrides?.customer?.plural ||
        DEFAULT_MDM_ENTITY_DISPLAY_NAMES.customer.plural,
    },
    supplier: {
      singular:
        overrides?.supplier?.singular ||
        DEFAULT_MDM_ENTITY_DISPLAY_NAMES.supplier.singular,
      plural:
        overrides?.supplier?.plural ||
        DEFAULT_MDM_ENTITY_DISPLAY_NAMES.supplier.plural,
    },
    distributor: {
      singular:
        overrides?.distributor?.singular ||
        DEFAULT_MDM_ENTITY_DISPLAY_NAMES.distributor.singular,
      plural:
        overrides?.distributor?.plural ||
        DEFAULT_MDM_ENTITY_DISPLAY_NAMES.distributor.plural,
    },
  };
}

export function hasMdmEntityDisplayNameOverride(
  overrides: MdmEntityDisplayNameOverrides,
  entityType: EntityType
): boolean {
  return Boolean(
    overrides?.[entityType]?.singular || overrides?.[entityType]?.plural
  );
}

export function getMdmEntityLabel(
  overrides: MdmEntityDisplayNameOverrides,
  entityType: EntityType,
  form: keyof MdmEntityDisplayNameForms,
  t: TFunction
): string {
  const customLabel = overrides?.[entityType]?.[form];
  if (customLabel) {
    return customLabel;
  }

  return t(DEFAULT_MDM_ENTITY_DISPLAY_NAMES[entityType][form]);
}

/**
 * Resolves a display label for an object-reference object type key
 * (customer / supplier / distributor / product / unknown).
 * Product is never configurable; company entities use storefront metadata.
 */
export function getObjectTypeDisplayLabel(
  objectType: string | undefined,
  overrides: MdmEntityDisplayNameOverrides,
  t: TFunction,
  form: keyof MdmEntityDisplayNameForms = "singular"
): string {
  if (!objectType) {
    return "-";
  }
  if (
    objectType === "customer" ||
    objectType === "supplier" ||
    objectType === "distributor"
  ) {
    return getMdmEntityLabel(overrides, objectType, form, t);
  }
  if (objectType === "product") {
    return form === "plural" ? t("Products") : t("Product");
  }
  return formatUnknownObjectType(objectType);
}

/**
 * Display-only remapping for system MDM default templates.
 * Stored DB names stay canonical; only the UI label changes.
 */
export function getMdmDefaultTemplateDisplayName(
  template: {
    template_name?: string | null;
    template_type?: string | null;
  },
  overrides: MdmEntityDisplayNameOverrides,
  t: TFunction,
  fallback = "Template"
): string {
  const name = template.template_name?.trim();
  if (!name) {
    return fallback;
  }

  const templateType = template.template_type;
  if (
    templateType !== "customer" &&
    templateType !== "supplier" &&
    templateType !== "distributor"
  ) {
    return name;
  }

  if (name !== CANONICAL_MDM_DEFAULT_TEMPLATE_NAMES[templateType]) {
    return name;
  }

  return `${getMdmEntityLabel(
    overrides,
    templateType,
    "singular",
    t
  )} Default Template`;
}

/**
 * Resolves configurable MDM company-entity display names from storefront metadata.
 * Product is intentionally excluded — it always uses its default name.
 * Attribute-driven labels (e.g. "Supplier ID") are not affected by this helper.
 */
export function useMdmEntityDisplayNames(): {
  getLabel: (
    entityType: EntityType,
    form?: keyof MdmEntityDisplayNameForms
  ) => string;
  getObjectTypeLabel: (
    objectType: string | undefined,
    form?: keyof MdmEntityDisplayNameForms
  ) => string;
  getTemplateDisplayName: (
    template: {
      template_name?: string | null;
      template_type?: string | null;
    },
    fallback?: string
  ) => string;
} {
  const { t } = useTranslation();
  const { storefront_metadata } = useStoreState();
  const overrides = useMemo(
    () => storefront_metadata?.mdm_entity_display_names,
    [storefront_metadata?.mdm_entity_display_names]
  );

  const getLabel = useCallback(
    (
      entityType: EntityType,
      form: keyof MdmEntityDisplayNameForms = "singular"
    ) => getMdmEntityLabel(overrides, entityType, form, t),
    [overrides, t]
  );

  const getObjectTypeLabel = useCallback(
    (
      objectType: string | undefined,
      form: keyof MdmEntityDisplayNameForms = "singular"
    ) => getObjectTypeDisplayLabel(objectType, overrides, t, form),
    [overrides, t]
  );

  const getTemplateDisplayName = useCallback(
    (
      template: {
        template_name?: string | null;
        template_type?: string | null;
      },
      fallback = "Template"
    ) => getMdmDefaultTemplateDisplayName(template, overrides, t, fallback),
    [overrides, t]
  );

  return {
    getLabel,
    getObjectTypeLabel,
    getTemplateDisplayName,
  };
}
